import React, { useCallback, useEffect, useMemo } from 'react'
import block from 'bem-cn'

import { DocumentTypes } from 'services/api/PaymentApi/types/common'

import { LocaleProfileType } from 'shared/locale/en-US'
import { IUser } from 'shared/models/User'

import { addNotify } from 'components/Notify'
import { Button, ButtonVariants } from 'components/UIKit/Button'
import { DragDropZone } from 'components/UIKit/DragDropZone'

import { getDocumentInfo, IDocumentItem } from './Document.utils'

import './Documents.scss'

interface IDocumentsProps {
  user: IUser | null
  documentList: IDocumentItem[]
  locale: LocaleProfileType
  uploadedFiles: File[] | null
  selectedDocument: DocumentTypes
  isUploadLoading: boolean
  changeDocumentType: (document: DocumentTypes | '') => void
  onSendDocument: () => void
  onChangeFile: (acceptedFiles: File[] | null) => void
  onCloseModal: () => void
}

const maxFileSizeBytes = 5 * 1024 * 1024

const b = block('documents')

const Documents: React.FC<IDocumentsProps> = ({
  documentList,
  locale,
  user,
  uploadedFiles,
  selectedDocument,
  isUploadLoading,
  onCloseModal,
  changeDocumentType,
  onSendDocument,
  onChangeFile,
}) => {
  const documentInfo = useMemo(() => {
    const document = documentList.find(item => item.value === selectedDocument) as IDocumentItem

    return getDocumentInfo({ document, locale })
  }, [documentList, locale, selectedDocument])

  const onSendClickHandler = useCallback(() => onSendDocument(), [onSendDocument])

  useEffect(() => () => onChangeFile(null), [])

  const callbackForLargeFile = useCallback(() => {
    addNotify(`${locale.fileIsTooBig}. Max ${(maxFileSizeBytes / 1024 / 1024).toFixed(0)}Mb`, 'error')
  }, [locale])

  const callbackForInvalidFile = useCallback(() => {
    addNotify(locale.fileTypeInvalid)
  }, [locale])

  const documentCards = useMemo(
    () =>
      documentInfo.items.map((doc, index) => {
        const id = doc.name

        return (
          <>
            <DragDropZone
              name={doc.name}
              onChange={onChangeFile}
              key={doc.name}
              maxSize={maxFileSizeBytes}
              callbackForLargeFile={callbackForLargeFile}
              callbackForInvalidFile={callbackForInvalidFile}>
              <div id={id} className={b('card')}>
                <span className={b('icon-wrapper')}>
                  <doc.img className={b('icon')} />
                </span>
                <div className={b('text-container')}>
                  <div className={b('text', { card: true })}>{doc.name}</div>
                  <div className={b('text', { sub: true })}>
                    {uploadedFiles && uploadedFiles[index] ? uploadedFiles[index].name : locale.dragDropOrClick}
                  </div>
                </div>
              </div>
            </DragDropZone>
            {doc.example && (
              <div className={b('example')}>
                <doc.example />
              </div>
            )}
          </>
        )
      }),
    [documentInfo.items, onChangeFile, uploadedFiles, locale, callbackForLargeFile],
  )

  return (
    <div className={b()}>
      <div className={b('header')}>
        <div className={b('header-title')}>{documentInfo.title}</div>
        <div className={b('header-sub-title')}>{documentInfo.subTitle}</div>
        <button onClick={onCloseModal} type="button" className={b('header-close')}>
          <div className={b('header-close-icon')}>×</div>
        </button>
      </div>
      <div className={b('content')}>
        <div className={b('cards-wrapper')}>{documentCards}</div>
        <div className={b('text')}>{documentInfo.text}</div>
        <div className={b('buttons-wrapper')}>
          <div className={b('button-wrapper')}>
            <Button
              variant={ButtonVariants.TERTIARY}
              text={locale.back}
              onClick={() => changeDocumentType('')}
              uppercase
            />
          </div>
          <div className={b('button-wrapper')}>
            <Button
              text={locale.send}
              onClick={onSendClickHandler}
              loading={isUploadLoading}
              disabled={isUploadLoading}
              uppercase
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Documents
